import { JournalEntry, Pattern, Decision, LifeContext, Language, AIPersonalityKey } from '../types';
import { getPersonality, DEFAULT_PERSONALITY_KEY } from '../shared/aiPersonalities';

export interface GeneratedInsight {
  type: 'trend_up' | 'trend_down' | 'discovery' | 'connection' | 'pattern' | 'goal' | 'energy';
  title: string;
  description: string;
  metric?: {
    value: number;
    label: string;
    change?: number;
  };
}

export interface DimensionTrendData {
  current: number;
  previous: number;
  history: number[];
}

export interface EmotionFlowData {
  date: string;
  displayDate: string;
  alignment: number;
  emotion: string;
  count: number;
}

export interface RhythmData {
  day: number;
  hour: 'morning' | 'afternoon' | 'evening' | 'night';
  value: number;
  count: number;
}

const translations = {
  Italian: {
    lastDays: 'ultimi {n} giorni',
    improved: 'è migliorato',
    declined: 'è calato',
    stable: 'è stabile',
    yourBest: 'Il tuo punto di forza',
    needsAttention: 'Richiede attenzione',
    discoveredPattern: 'Pattern scoperto',
    connectionWith: 'La connessione con',
    positiveMood: 'influenza positivamente il tuo umore',
    alignment: 'allineamento',
    moments: 'momenti',
    entries: 'entries',
    weeklyProgress: 'Progresso settimanale',
    comparedToBefore: 'rispetto alla settimana precedente',
    health: 'Salute',
    focus: 'Focus',
    connection: 'Connessione',
    spirit: 'Spirito',
    finance: 'Finanza',
    moreThan: 'più di',
    lessThan: 'meno di',
    focusImproving: 'Il tuo Focus sta migliorando',
    keepGoingWith: 'Continua così con',
    yourGoalProgress: 'Progresso verso i tuoi obiettivi',
    averageAlignment: 'Allineamento medio',
    strongestArea: 'Area più forte',
    weakestArea: 'Area da sviluppare',
    energyPeak: 'Picco di energia',
    bestTimeIs: 'Il tuo momento migliore è',
    peopleMatter: 'Le persone contano',
    youMentioned: 'Hai menzionato',
    times: 'volte',
    basedOn: 'Basato su',
    entriesTracked: 'momenti tracciati',
    coreAlignment: 'Allineamento Core',
    impactOnGoals: 'Impatto sui tuoi obiettivi',
    positive: 'positivo',
    negative: 'negativo',
    thisWeek: 'questa settimana',
    youAre: 'Stai',
    aligned: 'allineato',
    withYourGoals: 'con i tuoi obiettivi'
  },
  English: {
    lastDays: 'last {n} days',
    improved: 'has improved',
    declined: 'has declined',
    stable: 'is stable',
    yourBest: 'Your strength',
    needsAttention: 'Needs attention',
    discoveredPattern: 'Discovered pattern',
    connectionWith: 'Your connection with',
    positiveMood: 'positively impacts your mood',
    alignment: 'alignment',
    moments: 'moments',
    entries: 'entries',
    weeklyProgress: 'Weekly progress',
    comparedToBefore: 'compared to last week',
    health: 'Health',
    focus: 'Focus',
    connection: 'Connection',
    spirit: 'Spirit',
    finance: 'Finance',
    moreThan: 'more than',
    lessThan: 'less than',
    focusImproving: 'Your Focus is improving',
    keepGoingWith: 'Keep going with',
    yourGoalProgress: 'Progress towards your goals',
    averageAlignment: 'Average alignment',
    strongestArea: 'Strongest area',
    weakestArea: 'Area to develop',
    energyPeak: 'Energy peak',
    bestTimeIs: 'Your best time is',
    peopleMatter: 'People matter',
    youMentioned: 'You mentioned',
    times: 'times',
    basedOn: 'Based on',
    entriesTracked: 'tracked moments',
    coreAlignment: 'Core Alignment',
    impactOnGoals: 'Impact on your goals',
    positive: 'positive',
    negative: 'negative',
    thisWeek: 'this week',
    youAre: 'You are',
    aligned: 'aligned',
    withYourGoals: 'with your goals'
  },
  Spanish: {
    lastDays: 'últimos {n} días',
    improved: 'ha mejorado',
    declined: 'ha bajado',
    stable: 'está estable',
    yourBest: 'Tu fortaleza',
    needsAttention: 'Necesita atención',
    discoveredPattern: 'Patrón descubierto',
    connectionWith: 'Tu conexión con',
    positiveMood: 'impacta positivamente tu ánimo',
    alignment: 'alineamiento',
    moments: 'momentos',
    entries: 'entradas',
    weeklyProgress: 'Progreso semanal',
    comparedToBefore: 'comparado con la semana anterior',
    health: 'Salud',
    focus: 'Enfoque',
    connection: 'Conexión',
    spirit: 'Espíritu',
    finance: 'Finanzas',
    moreThan: 'más de',
    lessThan: 'menos de',
    focusImproving: 'Tu Enfoque está mejorando',
    keepGoingWith: 'Sigue así con',
    yourGoalProgress: 'Progreso hacia tus metas',
    averageAlignment: 'Alineamiento promedio',
    strongestArea: 'Área más fuerte',
    weakestArea: 'Área a desarrollar',
    energyPeak: 'Pico de energía',
    bestTimeIs: 'Tu mejor momento es',
    peopleMatter: 'Las personas importan',
    youMentioned: 'Mencionaste',
    times: 'veces',
    basedOn: 'Basado en',
    entriesTracked: 'momentos rastreados',
    coreAlignment: 'Alineamiento Core',
    impactOnGoals: 'Impacto en tus metas',
    positive: 'positivo',
    negative: 'negativo',
    thisWeek: 'esta semana',
    youAre: 'Estás',
    aligned: 'alineado',
    withYourGoals: 'con tus metas'
  },
  German: {
    lastDays: 'letzten {n} Tage',
    improved: 'hat sich verbessert',
    declined: 'ist gesunken',
    stable: 'ist stabil',
    yourBest: 'Deine Stärke',
    needsAttention: 'Braucht Aufmerksamkeit',
    discoveredPattern: 'Entdecktes Muster',
    connectionWith: 'Deine Verbindung mit',
    positiveMood: 'beeinflusst positiv deine Stimmung',
    alignment: 'Ausrichtung',
    moments: 'Momente',
    entries: 'Einträge',
    weeklyProgress: 'Wöchentlicher Fortschritt',
    comparedToBefore: 'im Vergleich zur letzten Woche',
    health: 'Gesundheit',
    focus: 'Fokus',
    connection: 'Verbindung',
    spirit: 'Geist',
    finance: 'Finanzen',
    moreThan: 'mehr als',
    lessThan: 'weniger als',
    focusImproving: 'Dein Fokus verbessert sich',
    keepGoingWith: 'Mach weiter mit',
    yourGoalProgress: 'Fortschritt zu deinen Zielen',
    averageAlignment: 'Durchschnittliche Ausrichtung',
    strongestArea: 'Stärkster Bereich',
    weakestArea: 'Bereich zum Entwickeln',
    energyPeak: 'Energiehoch',
    bestTimeIs: 'Deine beste Zeit ist',
    peopleMatter: 'Menschen zählen',
    youMentioned: 'Du hast erwähnt',
    times: 'mal',
    basedOn: 'Basierend auf',
    entriesTracked: 'getrackte Momente',
    coreAlignment: 'Kern-Ausrichtung',
    impactOnGoals: 'Auswirkung auf deine Ziele',
    positive: 'positiv',
    negative: 'negativ',
    thisWeek: 'diese Woche',
    youAre: 'Du bist',
    aligned: 'ausgerichtet',
    withYourGoals: 'mit deinen Zielen'
  },
  French: {
    lastDays: '{n} derniers jours',
    improved: "s'est amélioré",
    declined: 'a diminué',
    stable: 'est stable',
    yourBest: 'Ton point fort',
    needsAttention: 'Nécessite attention',
    discoveredPattern: 'Pattern découvert',
    connectionWith: 'Ta connexion avec',
    positiveMood: 'impacte positivement ton humeur',
    alignment: 'alignement',
    moments: 'moments',
    entries: 'entrées',
    weeklyProgress: 'Progrès hebdomadaire',
    comparedToBefore: 'par rapport à la semaine dernière',
    health: 'Santé',
    focus: 'Focus',
    connection: 'Connexion',
    spirit: 'Esprit',
    finance: 'Finance',
    moreThan: 'plus de',
    lessThan: 'moins de',
    focusImproving: 'Ton Focus s\'améliore',
    keepGoingWith: 'Continue comme ça avec',
    yourGoalProgress: 'Progrès vers tes objectifs',
    averageAlignment: 'Alignement moyen',
    strongestArea: 'Zone la plus forte',
    weakestArea: 'Zone à développer',
    energyPeak: "Pic d'énergie",
    bestTimeIs: 'Ton meilleur moment est',
    peopleMatter: 'Les gens comptent',
    youMentioned: 'Tu as mentionné',
    times: 'fois',
    basedOn: 'Basé sur',
    entriesTracked: 'moments suivis',
    coreAlignment: 'Alignement Core',
    impactOnGoals: 'Impact sur tes objectifs',
    positive: 'positif',
    negative: 'négatif',
    thisWeek: 'cette semaine',
    youAre: 'Tu es',
    aligned: 'aligné',
    withYourGoals: 'avec tes objectifs'
  }
};

const getTimestampMs = (ts: number | string): number => {
  if (typeof ts === 'string') return new Date(ts).getTime();
  return ts < 1e12 ? ts * 1000 : ts;
};

const categoryToDimension: Record<string, string> = {
  'Salute': 'salute', 'Health': 'salute', 'Santé': 'salute', 'Gesundheit': 'salute', 'Salud': 'salute', 'Body': 'salute',
  'Focus': 'focus', 'Mind': 'focus', 'Concentration': 'focus', 'Konzentration': 'focus', 'Enfoque': 'focus',
  'Connessione': 'connessione', 'Connection': 'connessione', 'Heart': 'connessione', 'Connexion': 'connessione', 'Verbindung': 'connessione', 'Conexión': 'connessione',
  'Spirito': 'spirito', 'Spirit': 'spirito', 'Soul': 'spirito', 'Esprit': 'spirito', 'Geist': 'spirito', 'Espíritu': 'spirito',
  'Finanza': 'finanza', 'Finance': 'finanza', 'Matter': 'finanza', 'Finances': 'finanza', 'Finanzen': 'finanza', 'Finanzas': 'finanza'
};

export const getDimensionTrends = (
  entries: JournalEntry[],
  _days: number = 14
): Record<string, DimensionTrendData> => {
  const now = Date.now();
  const DAY_MS = 1000 * 60 * 60 * 24;

  // Use ALL entries for dimension calculation
  const allEntries = entries;

  // For trend comparison: last 7 days vs previous 7 days
  const last7Days = entries.filter(e =>
    (now - getTimestampMs(e.timestamp)) < 7 * DAY_MS
  );
  const previous7Days = entries.filter(e => {
    const ts = getTimestampMs(e.timestamp);
    return (now - ts) >= 7 * DAY_MS && (now - ts) < 14 * DAY_MS;
  });

  const dimensions: Array<'salute' | 'focus' | 'connessione' | 'spirito' | 'finanza'> =
    ['salute', 'focus', 'connessione', 'spirito', 'finanza'];

  const result: Record<string, DimensionTrendData> = {};

  const calcDimensionScore = (entries: JournalEntry[], dim: string): number => {
    const dimEntries = entries.filter(e => {
      if (e.dimensionImpacts && e.dimensionImpacts[dim as keyof typeof e.dimensionImpacts] !== undefined) {
        return true;
      }
      const mappedDim = categoryToDimension[e.category];
      return mappedDim === dim;
    });

    if (dimEntries.length === 0) return 0;

    let total = 0;
    let count = 0;

    dimEntries.forEach(e => {
      if (e.dimensionImpacts && e.dimensionImpacts[dim as keyof typeof e.dimensionImpacts] !== undefined) {
        const impact = e.dimensionImpacts[dim as keyof typeof e.dimensionImpacts];
        total += Math.max(0, Math.min(100, 50 + (impact * 5)));
        count++;
      } else {
        total += Math.max(0, Math.min(100, e.goalAlignment || 50));
        count++;
      }
    });

    return count > 0 ? Math.min(100, Math.round(total / count)) : 0;
  };

  for (const dim of dimensions) {
    // Current score uses ALL entries
    const currentScore = calcDimensionScore(allEntries, dim);
    // Previous score for trend comparison (last 7 days vs previous 7 days)
    const recentScore = calcDimensionScore(last7Days, dim);
    const previousScore = calcDimensionScore(previous7Days, dim) || recentScore;

    // Build sparkline from ALL entries, grouped into ~10 buckets
    const dimEntries = allEntries.filter(e => {
      if (e.dimensionImpacts && e.dimensionImpacts[dim as keyof typeof e.dimensionImpacts] !== undefined) {
        return true;
      }
      const mappedDim = categoryToDimension[e.category];
      return mappedDim === dim;
    });

    const historyData: number[] = [];

    if (dimEntries.length > 0) {
      // Sort entries by timestamp
      const sortedEntries = [...dimEntries].sort((a, b) =>
        getTimestampMs(a.timestamp) - getTimestampMs(b.timestamp)
      );

      // Create ~10 time buckets spanning all data
      const numBuckets = Math.min(10, sortedEntries.length);
      const entriesPerBucket = Math.ceil(sortedEntries.length / numBuckets);

      for (let i = 0; i < numBuckets; i++) {
        const bucketStart = i * entriesPerBucket;
        const bucketEnd = Math.min((i + 1) * entriesPerBucket, sortedEntries.length);
        const bucketEntries = sortedEntries.slice(bucketStart, bucketEnd);

        if (bucketEntries.length > 0) {
          const bucketScore = calcDimensionScore(bucketEntries, dim);
          historyData.push(bucketScore);
        }
      }
    }

    result[dim] = {
      current: currentScore,
      previous: previousScore,
      history: historyData
    };
  }

  return result;
};

export const getEmotionFlowData = (
  entries: JournalEntry[],
  days: number = 14,
  language: Language = 'Italian'
): EmotionFlowData[] => {
  const now = Date.now();
  const DAY_MS = 1000 * 60 * 60 * 24;
  
  const result: EmotionFlowData[] = [];
  
  const monthNames = {
    Italian: ['Gen', 'Feb', 'Mar', 'Apr', 'Mag', 'Giu', 'Lug', 'Ago', 'Set', 'Ott', 'Nov', 'Dic'],
    English: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    Spanish: ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'],
    German: ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez'],
    French: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Juin', 'Juil', 'Août', 'Sep', 'Oct', 'Nov', 'Déc']
  };
  const months = monthNames[language] || monthNames.English;

  for (let i = days - 1; i >= 0; i--) {
    const dayStart = now - (i + 1) * DAY_MS;
    const dayEnd = now - i * DAY_MS;
    const date = new Date(dayEnd);
    
    const dayEntries = entries.filter(e => {
      const ts = getTimestampMs(e.timestamp);
      return ts >= dayStart && ts < dayEnd;
    });

    if (dayEntries.length > 0) {
      const avgAlignment = Math.round(
        dayEntries.reduce((sum, e) => sum + (e.goalAlignment || 50), 0) / dayEntries.length
      );
      
      const emotionCounts: Record<string, number> = {};
      dayEntries.forEach(e => {
        emotionCounts[e.emotion] = (emotionCounts[e.emotion] || 0) + 1;
      });
      const topEmotion = Object.entries(emotionCounts)
        .sort(([,a], [,b]) => b - a)[0]?.[0] || '😊';

      result.push({
        date: date.toISOString().split('T')[0],
        displayDate: `${date.getDate()} ${months[date.getMonth()]}`,
        alignment: avgAlignment,
        emotion: topEmotion,
        count: dayEntries.length
      });
    }
  }

  return result;
};

// Helper to calculate time of day from timestamp
const getTimeOfDayFromTimestamp = (timestamp: Date | string | number): 'morning' | 'afternoon' | 'evening' | 'night' => {
  const date = new Date(getTimestampMs(timestamp));
  const hour = date.getHours();

  if (hour >= 5 && hour < 12) return 'morning';
  if (hour >= 12 && hour < 17) return 'afternoon';
  if (hour >= 17 && hour < 21) return 'evening';
  return 'night';
};

export const getWeeklyRhythmData = (entries: JournalEntry[]): RhythmData[] => {
  const rhythmMap: Record<string, { total: number; count: number }> = {};

  entries.forEach(e => {
    const day = e.dayOfWeek ?? new Date(getTimestampMs(e.timestamp)).getDay();
    // Calculate timeOfDay from timestamp if not stored, instead of defaulting to afternoon
    const hour = e.timeOfDay || getTimeOfDayFromTimestamp(e.timestamp);
    const key = `${day}-${hour}`;
    
    if (!rhythmMap[key]) {
      rhythmMap[key] = { total: 0, count: 0 };
    }
    rhythmMap[key].total += e.goalAlignment || 50;
    rhythmMap[key].count += 1;
  });

  const result: RhythmData[] = [];
  for (let day = 0; day < 7; day++) {
    for (const hour of ['morning', 'afternoon', 'evening', 'night'] as const) {
      const key = `${day}-${hour}`;
      const data = rhythmMap[key] || { total: 0, count: 0 };
      result.push({
        day,
        hour,
        value: data.count > 0 ? Math.round(data.total / data.count) : 0,
        count: data.count
      });
    }
  }

  return result;
};

export const generateNarrativeInsights = (
  entries: JournalEntry[],
  patterns: Pattern[],
  decisions: Decision[],
  context: LifeContext,
  language: Language = 'Italian',
  personalityKey?: AIPersonalityKey
): GeneratedInsight[] => {
  const t = translations[language] || translations.Italian;
  // Get personality for tone adjustment (used for future enhancements)
  const _personality = getPersonality(personalityKey || DEFAULT_PERSONALITY_KEY);
  const insights: GeneratedInsight[] = [];
  const now = Date.now();
  const DAY_MS = 1000 * 60 * 60 * 24;
  const WEEK_MS = 7 * DAY_MS;

  const recentEntries = entries.filter(e => (now - getTimestampMs(e.timestamp)) < WEEK_MS);
  const previousWeekEntries = entries.filter(e => {
    const ts = getTimestampMs(e.timestamp);
    return (now - ts) >= WEEK_MS && (now - ts) < 2 * WEEK_MS;
  });

  const avgAlignment = recentEntries.length > 0
    ? Math.round(recentEntries.reduce((sum, e) => sum + (e.goalAlignment || 50), 0) / recentEntries.length)
    : 0;

  const prevAvg = previousWeekEntries.length > 0
    ? Math.round(previousWeekEntries.reduce((sum, e) => sum + (e.goalAlignment || 50), 0) / previousWeekEntries.length)
    : avgAlignment;

  if (recentEntries.length > 0) {
    insights.push({
      type: avgAlignment >= prevAvg ? 'trend_up' : 'trend_down',
      title: t.weeklyProgress,
      description: `${t.averageAlignment} ${t.lastDays.replace('{n}', '7')}`,
      metric: {
        value: avgAlignment,
        label: '%',
        change: avgAlignment - prevAvg
      }
    });
  }

  const positivePatterns = patterns.filter(p => p.isPositive && p.strength > 60);
  if (positivePatterns.length > 0) {
    const top = positivePatterns[0];
    insights.push({
      type: 'pattern',
      title: t.discoveredPattern,
      description: top.title,
      metric: {
        value: top.strength,
        label: '%'
      }
    });
  }

  const categoryStats: Record<string, { count: number; totalAlignment: number; best: number; worst: number }> = {};
  entries.forEach(e => {
    const cat = e.category;
    if (!categoryStats[cat]) {
      categoryStats[cat] = { count: 0, totalAlignment: 0, best: 0, worst: 100 };
    }
    categoryStats[cat].count++;
    categoryStats[cat].totalAlignment += e.goalAlignment || 50;
    categoryStats[cat].best = Math.max(categoryStats[cat].best, e.goalAlignment || 50);
    categoryStats[cat].worst = Math.min(categoryStats[cat].worst, e.goalAlignment || 50);
  });

  const sortedCategories = Object.entries(categoryStats)
    .filter(([_, data]) => data.count >= 2)
    .map(([cat, data]) => ({
      category: cat,
      avgAlignment: Math.round(data.totalAlignment / data.count),
      count: data.count,
      best: data.best,
      worst: data.worst
    }))
    .sort((a, b) => b.avgAlignment - a.avgAlignment);

  if (sortedCategories.length > 0) {
    const strongest = sortedCategories[0];
    insights.push({
      type: 'goal',
      title: t.strongestArea,
      description: `${strongest.category} (${strongest.count} ${t.moments})`,
      metric: {
        value: strongest.avgAlignment,
        label: '%'
      }
    });
  }

  if (sortedCategories.length > 1) {
    const weakest = sortedCategories[sortedCategories.length - 1];
    if (weakest.avgAlignment < 50) {
      insights.push({
        type: 'trend_down',
        title: t.weakestArea,
        description: `${weakest.category} ${t.needsAttention.toLowerCase()}`,
        metric: {
          value: weakest.avgAlignment,
          label: '%'
        }
      });
    }
  }

  const peopleMentions: Record<string, { count: number; totalMood: number }> = {};
  entries.forEach(e => {
    e.people?.forEach(p => {
      if (!peopleMentions[p.name]) {
        peopleMentions[p.name] = { count: 0, totalMood: 0 };
      }
      peopleMentions[p.name].count += 1;
      peopleMentions[p.name].totalMood += p.impactOnMood || 0;
    });
  });

  const topPeople = Object.entries(peopleMentions)
    .filter(([_, data]) => data.count >= 2 && data.totalMood > 0)
    .sort(([,a], [,b]) => b.totalMood - a.totalMood)
    .slice(0, 1);

  if (topPeople.length > 0) {
    const [name, data] = topPeople[0];
    insights.push({
      type: 'connection',
      title: `${t.connectionWith} ${name}`,
      description: t.positiveMood,
      metric: {
        value: data.count,
        label: t.moments
      }
    });
  }

  return insights.slice(0, 6);
};

export const getGoalProgress = (
  entries: JournalEntry[],
  goals: string[]
): Array<{ name: string; progress: number; color: string }> => {
  if (goals.length === 0) return [];

  // Use ALL entries, not filtered by time
  const allEntries = entries;

  // Extended color palette for more goals
  const goalColors = ['#6366f1', '#8b5cf6', '#a855f7', '#ec4899', '#10b981', '#f59e0b', '#ef4444', '#06b6d4', '#84cc16', '#f97316'];

  // Process all goals
  return goals.map((goal, index) => {
    const goalLower = goal.toLowerCase();

    const goalWords = goalLower.split(/\s+/).filter(w => w.length > 2);

    const relatedEntries = allEntries.filter(e => {
      const content = e.content.toLowerCase();
      const tags = (e.tags || []).map(t => t.toLowerCase()).join(' ');
      const category = (e.category || '').toLowerCase();
      const insight = (e.aiInsight || '').toLowerCase();

      return goalWords.some(word =>
        content.includes(word) ||
        tags.includes(word) ||
        category.includes(word) ||
        insight.includes(word)
      );
    });

    let progress: number;

    if (relatedEntries.length > 0) {
      const avgAlignment = relatedEntries.reduce((sum, e) => sum + (e.goalAlignment || 50), 0) / relatedEntries.length;
      // Frequency multiplier based on total entries, capped lower to avoid inflating scores
      const frequencyMultiplier = Math.min(1.10, 1 + (relatedEntries.length * 0.005));
      progress = Math.round(Math.min(100, avgAlignment * frequencyMultiplier));
    } else if (allEntries.length > 0) {
      const overallAvg = allEntries.reduce((sum, e) => sum + (e.goalAlignment || 50), 0) / allEntries.length;
      progress = Math.round(Math.max(10, overallAvg * 0.5));
    } else {
      progress = 0;
    }

    return {
      name: goal,
      progress,
      color: goalColors[index % goalColors.length]
    };
  });
};
